import { kebabCase } from "lodash-es"
import { observable, reaction, runInAction } from "mobx"

import { branchDisplayName } from "@/lib/displayUtils.ts"
import { resetErrorOnChange } from "@/lib/formUtils.ts"
import { Model } from "@/lib/Model.ts"

import type { AiAgentTask, AiAgentTaskHistoryItem, CPrincipal } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GitHubModel } from "@/routes/tasks/TaskViewScene/GitHubModel.ts"
import type { ChangeEvent } from "react"

export class CreatePrDialogModel extends Model {
  readonly branchName = observable.box("")
  readonly branchManuallyEdited = observable.box(false)
  readonly branchError = observable.box<string | null>(null)

  readonly targetBranch = observable.box<string | null>(null)

  readonly title = observable.box("")
  readonly titleError = observable.box<string | null>(null)

  readonly description = observable.box("")

  readonly isCreating = observable.box(false)

  private readonly defaultBranchName = () => {
    const versions = this.historyItems
    const activeItems = versions.filter(it => it.isActive)
    const defaultVersion = activeItems.length > 0 ? activeItems[activeItems.length - 1].number : null
    const defaultTitle = this.task?.title ?? ""
    return defaultVersion ? `${kebabCase(defaultTitle)}-v${defaultVersion}` : ""
  }

  private readonly getFooter = () => {
    return `\n\n———\nCreated by ${this.author.name} with Matter by JetBrains\n${this.taskLink}`
  }

  constructor(
    private readonly gitHubModel: GitHubModel,
    private readonly task: AiAgentTask,
    private readonly author: CPrincipal,
    private readonly taskLink: string,
    private readonly historyItems: AiAgentTaskHistoryItem[],
    private readonly onOpenChange: (open: boolean) => void
  ) {
    super("CreatePrDialogModel")

    const initialBranch = branchDisplayName(task.initialBranch ?? "")
    if (initialBranch.startsWith("matter/") || initialBranch.startsWith("ai-agent-task/")) {
      // Can't use internal Matter branches as a target
      void gitHubModel.getDefaultBranch().then(branch => {
        runInAction(() => this.targetBranch.set(branch))
      })
    } else {
      this.targetBranch.set(initialBranch)
    }

    resetErrorOnChange(this.lifetime, this.branchError, this.branchName)
    resetErrorOnChange(this.lifetime, this.titleError, this.title)

    if (!this.task.initRuntimeConfigState && !this.task.initProjectState) {
      this.prefillDefaults()
    }
  }

  private prefillDefaults() {
    if (!this.branchManuallyEdited.get()) {
      runInAction(() => this.branchName.set(this.defaultBranchName()))
    }

    const defaultTitle = this.task?.title ?? ""
    if (!this.title.get()) {
      runInAction(() => this.title.set(defaultTitle))
    }

    this.lifetime.add(
      reaction(() => this.gitHubModel.shortSummary.get(), shortSummary => {
          if (shortSummary) {
            runInAction(() => this.description.set(shortSummary + this.getFooter()))
          }
        },
        { fireImmediately: true }
      ), "updateDescription"
    )
  }

  readonly handleBranchChange = (e: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      this.branchName.set(e.target.value)
      this.branchManuallyEdited.set(true)
    })
  }

  readonly handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => this.title.set(e.target.value))
  }

  readonly handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    runInAction(() => this.description.set(e.target.value))
  }

  private validate(): boolean {
    const branch = this.branchName.get().trim()
    const prTitle = this.title.get().trim()

    let isValid = true

    if (!branch) {
      runInAction(() => this.branchError.set("Branch name is required"))
      isValid = false
    }

    if (!prTitle) {
      runInAction(() => this.titleError.set("Title is required"))
      isValid = false
    }

    return isValid
  }

  readonly submit = async () => {
    if (this.isCreating.get()) return

    if (!this.validate()) return

    try {
      runInAction(() => this.isCreating.set(true))

      const branchName = this.branchName.get().trim()
      const title = this.title.get().trim()
      const description = this.description.get().trim()
      const shortSummary = this.gitHubModel.shortSummary.get()

      const result = await this.gitHubModel.createPr(branchName, title, description, shortSummary)
      if (result && result.className === "Success") {
        this.reset()
        this.onOpenChange(false)
      }
    } finally {
      runInAction(() => this.isCreating.set(false))
    }
  }

  readonly reset = () => {
    runInAction(() => {
      this.branchName.set("")
      this.branchManuallyEdited.set(false)
      this.branchError.set(null)

      this.title.set("")
      this.titleError.set(null)

      this.description.set("")
    })
  }
}